import { useState, useEffect } from 'react';

const isFocusedInsideTarget = (target, includeBody = false) => {
  const focused = document.activeElement;

  if (target && !includeBody) {
    return target === focused || target.contains(focused);
  }

  return target && (target === focused || target.contains(focused) || document.body === focused);
};

const getTargetFromRef = (ref, selector) => (selector ? ref.current.querySelector(selector) : ref.current);

export const useIsFocused = (ref, selector = false, childSelector = '*[tabindex],input') => {
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!ref || !ref.current || isFocused) {
      return;
    }

    const targetContainer = getTargetFromRef(ref, selector);
    if (isFocusedInsideTarget(targetContainer)) {
      document.activeElement.blur();
    }
  }, [isFocused]);

  useEffect(() => {
    if (!ref || !ref.current) {
      return;
    }

    const targetContainer = getTargetFromRef(ref, selector);
    const elements = [targetContainer, ...targetContainer.querySelectorAll(childSelector)];

    const focusHandler = () => {
      if (isFocused) {
        return;
      }

      setIsFocused(true);
    };

    const click = ({ target }) => {
      if (target !== targetContainer && !targetContainer.contains(target)) {
        setIsFocused(false);
      }
    };

    const blurHandler = () => {
      if (!isFocusedInsideTarget(targetContainer, true)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('click', click);

    elements.forEach(el => {
      el.addEventListener('focus', focusHandler);
      el.addEventListener('blur', blurHandler);
    });

    return () => {
      document.removeEventListener('click', click);

      elements.forEach(el => {
        el.removeEventListener('focus', focusHandler);
        el.removeEventListener('blur', blurHandler);
      });
    };
  });

  return {
    isFocused,
    blur: () => setIsFocused(false),
    focus: () => setIsFocused(true)
  };
};
